import { mapPayerNameToCodeForDropdown } from "constants/practiceType";
import {
  IPracticeTypeSuggestion,
  IPracticeTypeSuggestionRule,
  PracticeTypeRuleModality,
  PracticeTypeRulePayerType,
} from "types/PracticeTypeSuggestion/PracticeTypeSuggestion";

/**
 * CLE-2971 — the words on Eligibility › Service Type Rules, in one place so the list, the dialog
 * and the tester say the same thing. Never a bare enum or code where a name exists.
 */

/** In the fixed CPT-group precedence order (CLE-3000), which is also the dropdown order. */
export const MODALITY_LABELS: Record<PracticeTypeRuleModality, string> = {
  MRI: "MRI",
  CT: "CT",
  ULTRASOUND: "Ultrasound",
  NUCLEAR_MEDICINE: "Nuclear medicine",
  SLEEP_STUDY: "Sleep study",
  OTHER_DIAGNOSTIC_TEST: "Other diagnostic test",
  PULMONARY_FUNCTION_TEST: "Pulmonary function test",
  XRAY: "X-ray",
};

/** "… because the estimate has {this} code". The original four keep their label as written. */
const MODALITY_CODE_PHRASES: Record<PracticeTypeRuleModality, string> = {
  MRI: "an MRI",
  CT: "a CT",
  ULTRASOUND: "an Ultrasound",
  NUCLEAR_MEDICINE: "a nuclear medicine",
  SLEEP_STUDY: "a sleep study",
  OTHER_DIAGNOSTIC_TEST: "a diagnostic test",
  PULMONARY_FUNCTION_TEST: "a pulmonary function test",
  XRAY: "an X-ray",
};

export const MODALITY_CHOICES = (
  Object.keys(MODALITY_LABELS) as PracticeTypeRuleModality[]
).map((value) => ({ label: MODALITY_LABELS[value], value }));

export const PAYER_TYPE_LABELS: Record<PracticeTypeRulePayerType, string> = {
  Commercial: "Commercial",
  UhcMedicareAdvantage: "Medicare Advantage",
  OriginalMedicare: "Medicare",
  Medicaid: "Medicaid",
};

export const PAYER_TYPE_CHOICES = (
  Object.keys(PAYER_TYPE_LABELS) as PracticeTypeRulePayerType[]
).map((value) => ({ label: PAYER_TYPE_LABELS[value], value }));

/** The Service Types a rule can run under — the CreateOrder dropdown's, name and code. */
export const SERVICE_TYPE_CHOICES = mapPayerNameToCodeForDropdown.map(
  (option) => ({
    label: `${option.name} (${option.value})`,
    value: option.value,
  })
);

/** "MRI/CAT Scan", or the bare code when it is not one the dropdown offers. */
export const serviceTypeName = (code: string): string =>
  mapPayerNameToCodeForDropdown.find((option) => option.value === code)?.name ??
  `Service Type ${code}`;

/** "MRI/CAT Scan (31)". */
export const serviceTypeLabel = (code: string): string =>
  `${serviceTypeName(code)} (${code})`;

/** The intro under the buttons. */
export const INTRO =
  "When an estimate has a CPT code in one of the CPT groups (MRI, CT, ultrasound, nuclear medicine, sleep study, other diagnostic test, X-ray — see the CPT Groups tab), these rules decide which Service Type its eligibility check runs under, so the right benefits line is read. A rule for all hospitals applies everywhere unless a hospital has its own enabled rule for the same procedure type — then the hospital's rule is used there instead. Within what applies, rules are checked from the lowest priority number up, and the first one that fits the payer and plan wins.";

export const ALL_HOSPITALS = "All hospitals";

/** Under both keyword fields in the dialog. Matching is by substring. */
export const KEYWORD_CAPTION =
  'Matches anywhere in the plan name pVerify returns: "PREFERRED CARE" also matches "PREFERRED CARE,PROVIDER-ADMINISTERED DRUG REVIEW". At least 3 characters. Type a word or phrase and press Enter.';

/** What the dialog and the API say about a keyword under 3 characters. */
export const SHORT_KEYWORD_PROBLEM = "Keywords need at least 3 characters.";

export const MIN_KEYWORD_LENGTH = 3;

export const scopeInWords = (
  rule: Pick<IPracticeTypeSuggestionRule, "accountId">,
  accountNames: Map<string, string>
): string =>
  rule.accountId === null
    ? ALL_HOSPITALS
    : accountNames.get(rule.accountId) ?? "A hospital that no longer exists";

export const payersInWords = (
  rule: Pick<IPracticeTypeSuggestionRule, "payerIds" | "payerTypes">,
  payerNames: Map<number, string>
): string => {
  const parts: string[] = [];
  if (rule.payerIds.length > 0) {
    parts.push(
      rule.payerIds.map((id) => payerNames.get(id) ?? `Payer #${id}`).join(", ")
    );
  }
  if (rule.payerTypes.length > 0) {
    parts.push(
      `${rule.payerTypes
        .map((type) => PAYER_TYPE_LABELS[type] ?? type)
        .join(" or ")} payers`
    );
  }
  return parts.length > 0 ? parts.join("; ") : "Any";
};

/** The tester's answer, in one sentence. */
export const suggestionInWords = (
  suggestion: IPracticeTypeSuggestion | null
): string => {
  if (!suggestion)
    return "No change — the check stays under the Service Type it ran under.";
  const target = `${
    suggestion.targetPracticeTypeName ??
    serviceTypeName(suggestion.targetPracticeTypeCode)
  } (${suggestion.targetPracticeTypeCode})`;
  const phrase =
    MODALITY_CODE_PHRASES[suggestion.modality as PracticeTypeRuleModality] ??
    `a ${suggestion.modality}`;
  return `Would switch to ${target} because the estimate has ${phrase} code${
    suggestion.reason ? `: ${suggestion.reason}` : "."
  }`;
};
